/**
 * 事件驱动 Handler 注册
 * 装饰器风格的 handler 定义和注册
 */
import type { CommandHandlerFn } from './command-handler'

/** Handler 元数据 */
export interface HandlerMeta {
  command: string
  description?: string
  timeout?: number
  cacheable?: boolean
  schema?: Record<string, any>
}

/** 注册的 Handler 项 */
export interface RegisteredHandler {
  meta: HandlerMeta
  handler: CommandHandlerFn
}

/**
 * Handler 注册表
 * 集中管理所有 handler 定义
 */
export class HandlerRegistry {
  private handlers = new Map<string, RegisteredHandler>()

  /** 注册 handler */
  register(meta: HandlerMeta, handler: CommandHandlerFn): void {
    this.handlers.set(meta.command, { meta, handler })
  }

  /** 链式注册 */
  on(command: string, handler: CommandHandlerFn, options?: Omit<HandlerMeta, 'command'>): HandlerRegistry {
    this.register({ command, ...options }, handler)
    return this
  }

  /** 获取 handler */
  get(command: string): RegisteredHandler | undefined {
    return this.handlers.get(command)
  }

  /** 检查是否存在 */
  has(command: string): boolean {
    return this.handlers.has(command)
  }

  /** 注销 handler */
  unregister(command: string): boolean {
    return this.handlers.delete(command)
  }

  /** 获取所有命令名 */
  getCommands(): string[] {
    return [...this.handlers.keys()]
  }

  /** 获取所有元数据 */
  getMetas(): HandlerMeta[] {
    return [...this.handlers.values()].map((h) => h.meta)
  }

  /** 转为 handlers map（用于传入 RemoteKitClient） */
  toHandlerMap(): Record<string, CommandHandlerFn> {
    const map: Record<string, CommandHandlerFn> = {}
    for (const [cmd, { handler }] of this.handlers) {
      map[cmd] = handler
    }
    return map
  }

  /** 合并另一个注册表 */
  merge(other: HandlerRegistry): void {
    for (const [cmd, entry] of other.handlers) {
      this.handlers.set(cmd, entry)
    }
  }

  /** 获取数量 */
  get size(): number {
    return this.handlers.size
  }

  /** 清空 */
  clear(): void {
    this.handlers.clear()
  }
}

/**
 * 创建 handler 注册表
 * @example
 * ```typescript
 * const handlers = createHandlerRegistry()
 *   .on('app:status', async () => ({ ok: true }))
 *   .on('app:restart', async () => { process.exit(0) }, { timeout: 5000 })
 * ```
 */
export function createHandlerRegistry(): HandlerRegistry {
  return new HandlerRegistry()
}
